import type {
  CrmContactIntakeAnswer,
  CrmContactIntakeAnswerSet,
  CrmContactIntakeAnswerTone,
} from "@ease/crm/contact-intake-answers";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/i18n";

interface CrmIntakeAnswersCardProps {
  sets: CrmContactIntakeAnswerSet[];
  title?: string;
  description?: string;
  className?: string;
}

export function CrmIntakeAnswersCard({
  sets,
  title = "Intake answers",
  description = "Tenant-approved qualification, onboarding, or intake responses for operator review.",
  className,
}: CrmIntakeAnswersCardProps) {
  if (sets.length === 0) return null;

  const answerCount = sets.reduce((total, set) => total + set.answers.length, 0);

  return (
    <section className={cn("rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4", className)}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">{title}</h3>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{description}</p>
        </div>
        <span className="shrink-0 rounded-full border border-[var(--color-border)] px-2.5 py-1 text-xs font-medium text-[var(--color-muted-foreground)]">
          {answerCount}
        </span>
      </div>
      <div className="mt-4 space-y-4">
        {sets.map((set) => (
          <section key={set.key} className="border-t border-[var(--color-border)] pt-4 first:border-t-0 first:pt-0">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="text-sm font-semibold text-[var(--color-foreground)]">{set.label}</h4>
                  {set.source ? <IntakeAnswerChip label={set.source} /> : null}
                  {set.status ? <IntakeAnswerChip label={set.status} tone={set.tone} /> : null}
                </div>
                {set.summary ? (
                  <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{set.summary}</p>
                ) : null}
              </div>
              {set.completedAt ? (
                <span className="shrink-0 text-xs text-[var(--color-muted-foreground)]">
                  {formatDate(set.completedAt)}
                </span>
              ) : null}
            </div>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              {set.answers.map((answer) => (
                <IntakeAnswerCell key={`${set.key}:${answer.key}`} answer={answer} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </section>
  );
}

function IntakeAnswerChip({
  label,
  tone = "neutral",
}: {
  label: string;
  tone?: CrmContactIntakeAnswerTone;
}) {
  return (
    <span className={cn(
      "rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize",
      getCrmIntakeAnswerToneClasses(tone),
    )}>
      {label.replaceAll("_", " ")}
    </span>
  );
}

function IntakeAnswerCell({ answer }: { answer: CrmContactIntakeAnswer }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[11px] font-semibold uppercase text-[var(--color-muted-foreground)]">{answer.label}</p>
          {answer.category ? (
            <p className="mt-0.5 text-[10px] uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
              {answer.category.replaceAll("_", " ")}
            </p>
          ) : null}
        </div>
        {answer.tone !== "neutral" ? (
          <span className={cn(
            "shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize",
            getCrmIntakeAnswerToneClasses(answer.tone),
          )}>
            {answer.tone}
          </span>
        ) : null}
      </div>
      <p className="mt-1 break-words text-sm text-[var(--color-foreground)]">{answer.value}</p>
      {answer.description ? (
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{answer.description}</p>
      ) : null}
    </div>
  );
}

export function getCrmIntakeAnswerToneClasses(tone: CrmContactIntakeAnswerTone) {
  if (tone === "success") {
    return "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]";
  }
  if (tone === "warning") {
    return "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
  }
  if (tone === "danger") {
    return "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";
  }
  if (tone === "info") {
    return "border-[var(--color-info)]/30 bg-[var(--color-info)]/10 text-[var(--color-info)]";
  }
  return "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)]";
}
